import {TextureType} from "@generated"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"

// Looks up `cache[textureType]`. On a hit, reuses the cached value with no recomputation. On a miss,
// runs `compute()` and writes the new value back into the returned cache — the caller persists it onto
// its own node (e.g. `this.node.xyzCache = cache`), typically from within `createImageOpGraph()`.
//
// Invalidation is push-based rather than compared: a present value is always trusted, because
// OperatorStack clears the whole cache (via Operator.invalidateUpstreamCaches(), triggered by
// OperatorCallback.notifyOperatorEdited) the instant something upstream actually changes.
//
// Reuse this for any operator whose graph depends on an expensive reduction over its own upstream
// sourceImage: add a `xyzCache?: CachedUpstreamValue<T>` field to the operator's persisted node,
// override `invalidateUpstreamCaches()` to clear it, write a small `computeXyz(imageOpSystem,
// sourceImage): Promise<T>`, and call this function once in createImageOpGraph() (see recolor's
// sourceStatsCache / highpass's sourceMeanCache for worked examples).
export async function getOrComputeCachedUpstreamValue<T>(
    textureType: TextureType,
    cache: TextureEditNodes.CachedUpstreamValue<T> | undefined,
    compute: () => Promise<T>,
): Promise<{value: T; cache: TextureEditNodes.CachedUpstreamValue<T>}> {
    const cached = cache?.[textureType]
    if (cached !== undefined) {
        return {
            value: cached,
            cache: cache ?? {},
        }
    }
    const value = await compute()
    return {
        value,
        cache: {
            ...cache,
            [textureType]: value,
        },
    }
}
